import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import SEO, { breadcrumbSchema } from "@/components/SEO";
import {
  Sparkles,
  Loader2,
  Send,
  CheckCircle2,
  User,
  Mail,
  Phone,
  Calendar,
  Users as UsersIcon,
  MapPin,
  ArrowRight,
} from "lucide-react";

const SERVICE_TYPES = [
  { value: "self_drive", label: "Self-drive Rental" },
  { value: "chauffeur", label: "Chauffeur Service" },
  { value: "airport", label: "Airport Pickup" },
  { value: "long_term", label: "Long-term Hire (1+ months)" },
  { value: "corporate", label: "Corporate / NGO Account" },
  { value: "event", label: "Wedding / Event / Tour" },
];

const VEHICLE_CATEGORIES = [
  { value: "sedan", label: "Sedan (Camry, Mazda 3)" },
  { value: "compact_suv", label: "Mid-size SUV (CR-V, Highlander, Outlander, Creta)" },
  { value: "full_suv", label: "Full-size SUV (Prado, Land Cruiser)" },
  { value: "other", label: "Not sure — please advise" },
];

export default function CustomQuotePage() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    service_type: "self_drive",
    vehicle_category: "sedan",
    pickup_location: "",
    destination: "",
    start_date: "",
    end_date: "",
    duration: "",
    passengers: "",
    budget: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill name, email, phone and your message.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        passengers: form.passengers ? parseInt(form.passengers) : null,
        website: "", // honeypot — bots fill this; we send it intentionally empty
      };
      const { data } = await api.post("/quote", payload);
      setSubmitted({ reference: data.reference });
      toast.success("Quote request received!");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not submit");
    } finally {
      setSubmitting(false);
    }
  };

  const copyReference = async () => {
    try {
      await navigator.clipboard.writeText(submitted.reference);
      toast.success("Reference copied to clipboard");
    } catch (_) {
      toast.error("Could not copy — please copy manually");
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[70vh] bg-[#f7f8fa] py-20" data-testid="quote-success">
        <div className="container-titan max-w-2xl">
          <div className="bg-white border border-gray-200 p-10 text-center">
            <CheckCircle2 className="mx-auto text-green-600" size={56} />
            <h1 className="font-display text-3xl font-bold text-[#0d2040] mt-6">
              Quote request received!
            </h1>
            <p className="text-gray-600 mt-3">
              Thank you. Our team will reach out within a few hours during business hours.
            </p>
            <div className="mt-6 inline-block border border-gray-200 bg-[#f7f8fa] px-6 py-3">
              <div className="text-xs uppercase tracking-widest text-gray-500">Your reference</div>
              <div className="flex items-center gap-2 mt-1">
                <div className="font-display text-2xl font-bold text-[#e63946]" data-testid="quote-reference">{submitted.reference}</div>
                <button
                  type="button"
                  onClick={copyReference}
                  className="text-xs uppercase tracking-widest text-gray-500 hover:text-[#0d2040] border border-gray-300 px-2 py-1"
                  data-testid="quote-reference-copy"
                  aria-label="Copy reference"
                >
                  Copy
                </button>
              </div>
            </div>
            <p className="text-sm text-gray-500 mt-6">
              Need to speak now? Call us:&nbsp;
              <a href="tel:+233540124247" className="font-bold text-[#0d2040] hover:text-[#e63946]">054 012 4247</a>
              &nbsp;/&nbsp;
              <a href="tel:+233201234999" className="font-bold text-[#0d2040] hover:text-[#e63946]">020 123 4999</a>
            </p>
            <div className="mt-8 flex gap-3 justify-center">
              <Link to="/" className="btn-outline-navy">Back home</Link>
              <Link to="/fleet" className="btn-primary">Browse fleet <ArrowRight size={14} /></Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="quote-page">
      <SEO
        title="Get a Custom Quote — Long-Term, Corporate & Event Car Hire in Ghana"
        description="Request a custom car rental quote from Titan. Long-term lease, chauffeur, corporate accounts, NGO contracts, weddings, events. Same-day team response on WhatsApp."
        path="/quote"
        keywords="custom car rental quote Ghana, corporate car hire Accra, NGO car rental, wedding car Ghana, long-term lease Ghana"
        jsonLd={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Quote", path: "/quote" }])}
      />
      <section className="bg-[#0d2040] text-white py-16 md:py-20">
        <div className="container-titan grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={18} className="text-[#ff8a93]" />
              <span className="eyebrow text-[#ff8a93]">Custom Quote</span>
            </div>
            <h1 className="h-section text-white">Need something tailored?</h1>
            <p className="text-white/75 mt-5 text-lg max-w-xl leading-relaxed">
              For long-term hire, corporate fleets, wedding parties, multi-vehicle bookings or any rental need that
              doesn&apos;t fit our standard rates — fill the form and our team will reply with a personalised quote.
            </p>
            <ul className="mt-7 grid sm:grid-cols-2 gap-3 max-w-md">
              {[
                "Discounts on 7+ day rentals",
                "Volume pricing for fleets",
                "Wedding & event packages",
                "NGO & embassy programs",
              ].map((b) => (
                <li key={b} className="flex items-center gap-2 text-sm text-white/80">
                  <span className="w-1.5 h-1.5 bg-[#e63946]" /> {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white/5 border border-white/10 p-7">
              <h3 className="font-display text-xl font-semibold">Prefer to talk?</h3>
              <p className="text-white/70 text-sm mt-2">We&apos;re here Mon–Sat 7:00–20:00.</p>
              <ul className="mt-5 space-y-3 text-sm">
                <li><span className="text-white/50 uppercase text-xs tracking-widest block">Call</span>
                  <a href="tel:+233540124247" className="block hover:text-[#ff8a93]">054 012 4247</a>
                  <a href="tel:+233201234999" className="block hover:text-[#ff8a93]">020 123 4999</a>
                </li>
                <li><span className="text-white/50 uppercase text-xs tracking-widest block">Email</span>
                  <a href="mailto:info@titancarrental.com" className="hover:text-[#ff8a93]">info@titancarrental.com</a>
                </li>
                <li><span className="text-white/50 uppercase text-xs tracking-widest block">Visit</span> 3 Maseru St., East Legon · Accra</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="section-pad">
        <div className="container-titan max-w-4xl">
          <form onSubmit={submit} className="bg-white border border-gray-200 p-7 md:p-10 booking-widget" data-testid="quote-form">
            {/* Honeypot — humans never see/fill this */}
            <input
              type="text"
              name="website"
              value={form.website || ""}
              onChange={(e) => update("website", e.target.value)}
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="hidden"
            />
            <h2 className="font-display text-2xl font-semibold text-[#0d2040]">Tell us what you need</h2>
            <p className="text-sm text-gray-600 mt-1">We typically reply within a few hours during business hours.</p>

            {/* Contact */}
            <div className="mt-7">
              <span className="eyebrow">Your details</span>
              <div className="grid md:grid-cols-3 gap-4 mt-3">
                <div>
                  <label className="titan-label"><User size={12} className="inline mr-1" /> Name *</label>
                  <input className="titan-input" value={form.name} onChange={(e) => update("name", e.target.value)} required data-testid="quote-name" />
                </div>
                <div>
                  <label className="titan-label"><Mail size={12} className="inline mr-1" /> Email *</label>
                  <input type="email" className="titan-input" value={form.email} onChange={(e) => update("email", e.target.value)} required data-testid="quote-email" />
                </div>
                <div>
                  <label className="titan-label"><Phone size={12} className="inline mr-1" /> Phone *</label>
                  <input className="titan-input" value={form.phone} onChange={(e) => update("phone", e.target.value)} required data-testid="quote-phone" />
                </div>
              </div>
            </div>

            {/* Service */}
            <div className="mt-7">
              <span className="eyebrow">What service?</span>
              <div className="grid md:grid-cols-2 gap-4 mt-3">
                <div>
                  <label className="titan-label">Service type *</label>
                  <select className="titan-input" value={form.service_type} onChange={(e) => update("service_type", e.target.value)} data-testid="quote-service-type">
                    {SERVICE_TYPES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="titan-label">Vehicle category</label>
                  <select className="titan-input" value={form.vehicle_category} onChange={(e) => update("vehicle_category", e.target.value)} data-testid="quote-vehicle-category">
                    {VEHICLE_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Trip */}
            <div className="mt-7">
              <span className="eyebrow">Trip details</span>
              <div className="grid md:grid-cols-2 gap-4 mt-3">
                <div>
                  <label className="titan-label"><MapPin size={12} className="inline mr-1" /> Pickup location</label>
                  <input className="titan-input" placeholder="East Legon, Airport…" value={form.pickup_location} onChange={(e) => update("pickup_location", e.target.value)} />
                </div>
                <div>
                  <label className="titan-label"><MapPin size={12} className="inline mr-1" /> Destination(s)</label>
                  <input className="titan-input" placeholder="Cape Coast, Kumasi…" value={form.destination} onChange={(e) => update("destination", e.target.value)} />
                </div>
                <div>
                  <label className="titan-label"><Calendar size={12} className="inline mr-1" /> Start date</label>
                  <input type="date" className="titan-input" value={form.start_date} onChange={(e) => update("start_date", e.target.value)} data-testid="quote-start-date" />
                </div>
                <div>
                  <label className="titan-label"><Calendar size={12} className="inline mr-1" /> End date</label>
                  <input type="date" className="titan-input" value={form.end_date} min={form.start_date} onChange={(e) => update("end_date", e.target.value)} data-testid="quote-end-date" />
                </div>
                <div>
                  <label className="titan-label">Duration (e.g. 2 weeks)</label>
                  <input className="titan-input" value={form.duration} onChange={(e) => update("duration", e.target.value)} />
                </div>
                <div>
                  <label className="titan-label"><UsersIcon size={12} className="inline mr-1" /> Passengers</label>
                  <input type="number" min="1" max="60" className="titan-input" value={form.passengers} onChange={(e) => update("passengers", e.target.value)} />
                </div>
              </div>
            </div>

            {/* Budget + message */}
            <div className="mt-7">
              <span className="eyebrow">Anything else?</span>
              <div className="grid gap-4 mt-3">
                <div>
                  <label className="titan-label">Budget (optional)</label>
                  <input className="titan-input" placeholder="e.g. GH₵ 5,000 total" value={form.budget} onChange={(e) => update("budget", e.target.value)} />
                </div>
                <div>
                  <label className="titan-label">Tell us about your trip *</label>
                  <textarea
                    rows={5}
                    className="titan-input"
                    placeholder="Wedding party of 25, need 2 SUVs + 1 Coaster bus for the day, full chauffeur service, pickup from Airport hotel…"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    required
                    data-testid="quote-message"
                  />
                </div>
              </div>
            </div>

            <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-4 pt-6 border-t border-gray-100">
              <p className="text-xs text-gray-500">
                By submitting, you agree that Titan Car Rentals may contact you about your request.
              </p>
              <button type="submit" disabled={submitting} className="btn-primary" data-testid="quote-submit-btn">
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <><Send size={14} /> Request my quote</>}
              </button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
